import { FieldAPI, FieldsetAPI, FormAPI, InputAPI, PhoneNumberAPI, RecaptchaAPI, WizardAPI, ModalService } from '@philips-internal/oswald-ui-components';
import Controller, { OnAfterNavigate, OnBlur, OnChange, OnKeyUp, OnMount, OnSubmit, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { MountEvent, NavigateEvent, ValidateEvent, ChangeEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import BaseController from '../BaseController';
import { Inject } from '@odinjs/odin';
import { Value } from '@philips-internal/oswald-ui-core';
import { cast } from '../../utils/cast-helpers';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import TermCheckApi from '@philips-emr/referring-physician-components/lib/components/term-checkbox/term-checkbox-api';
import AccountCreationBlackboard from './account-creation-blackboard';
import { getCatpchaPublicKey } from '../../utils/captcha-utils';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { Country } from '@philips-internal/oswald-ui-constants';
import { isEmptyObject } from '@philips-internal/oswald-ui-utils';
import { confirmationFieldValidator, CONFIRMATION_FIELD_VALID, optionsValidator } from '../../utils/user-util.js';
import { AccountCreationStepsAliases } from '../../codegen/constants/aliases/account-creation-steps-aliases.js';

export enum StepNames {
  PersonalAccount = 'step-account-information',
  PersonalAddress = 'step-personal-address',
  PersonalIdentification = 'step-personal-identification',
  PersonalInformation = 'step-personal-information'
}

interface PrivacyNotice {
  termSeqNr: number,
  termName: string,
  termDescription: string,
}

type TermOfUser = PrivacyNotice

@Controller({ fragment: 'account-creation-steps', component: 'layout-root' })
export default class AccountCreationStepsController extends BaseController {

  @Inject('form-personal-identification')
  preRegistrationForm: FormAPI;

  @Inject('form-personal-information')
  informationForm: FormAPI;

  @Inject('form-address')
  addressForm: FormAPI;

  @Inject('form-account-information')
  accountForm: FormAPI;

  @Inject('checkbox-agree-terms')
  checkboxAgreeTerms: TermCheckApi;

  @Inject('AccountCreationBlackboard')
  accountCreationBlackboard: AccountCreationBlackboard;

  @Inject('recaptcha')
  captcha: RecaptchaAPI;

  @Inject('wizard-account-creation')
  stepsAccountCreation: WizardAPI;

  @Inject('field-state')
  license: FieldAPI;

  @Inject('field-medical')
  specialty: FieldAPI;

  @Inject('input-taxpayer')
  taxpayer: InputAPI;

  @Inject('new-user-personal-information')
  gender: FieldAPI;

  @Inject('input-phone')
  inputPhone: PhoneNumberAPI;

  @Inject('field-personal-state')
  personalState: FieldAPI;

  @Inject('input-postal')
  postal: InputAPI;

  @Inject('field-email')
  emailField: FieldAPI;

  @Inject('input-email')
  inputEmail: InputAPI;

  @Inject('input-confirm-email')
  inputConfirmEmail: InputAPI;

  @Inject(AccountCreationStepsAliases.FIELD_CONFIRM_EMAIL)
  fieldConfirmEmail: FieldAPI;

  @Inject('input-confirm-password')
  inputConfirmPassword: InputAPI;

  @Inject('field-confirm-password')
  fieldConfirmPassword: FieldAPI;

  @Inject('fieldset-account-data')
  fieldset: FieldsetAPI;

  allForms: FormAPI[] = [];
  email: string;
  confirmEmail: string;
  password: string;
  confirmPassword: string;
  emailAlreadyExists = false;
  validPrivacyAndTermOfUser = true;

  @OnAfterNavigate({ target: 'wizard-account-creation' })
  onMainWizardAfterNavigate(event: NavigateEvent<string>): void {
    switch (event.to) {
      case StepNames.PersonalIdentification: {
        this.preRegistrationForm.setEditing(true);
        break;
      }
      case StepNames.PersonalInformation: {
        this.preRegistrationForm.setEditing(false);
        this.informationForm.setEditing(true);
        break;
      }
      case StepNames.PersonalAddress: {
        this.informationForm.setEditing(false);
        this.addressForm.setEditing(true);
        break;
      }
      case StepNames.PersonalAccount: {
        this.addressForm.setEditing(false);
        this.accountForm.setEditing(true);
        break;
      }
    }
  }

  @OnMount({ target: 'recaptcha' })
  async onMountRecaptcha(event: MountEvent): Promise<void> {
    const recaptcha = event.target as RecaptchaAPI;
    const publicKey = await getCatpchaPublicKey();
    recaptcha.setSiteKey(publicKey);
  }

  @OnMount({ target: 'step-personal-information' })
  updateDdi(): void {
    this.updateDefaultPhoneDdi();
  }

  @OnMount({ target: ['form-personal-identification', 'form-personal-information', 'form-address', 'form-account-information'] })
  onMountAllForms(evt: MountEvent): void {
    this.allForms.push(cast(evt.target));
    this.addValidators();
  }

  @OnMount({ target: 'checkbox-agree-terms' })
  onTermo(): void {
    this.checkboxAgreeTerms.setEnabled(true);
    this.checkboxAgreeTerms.onClickTermo.listen(() => this.onClickTerm());
    this.checkboxAgreeTerms.onClickAviso.listen(() => this.onClickPrivacy());

    this.validateIfTermOfUserIsDefined();
    this.validateIfPrivacyIsDefined();
  }

  validateIfTermOfUserIsDefined(): void {
    HttpService.get(APPLICATION_ENDPOINTS.CREATE_NEW_ACCOUNT_PRIVACY_NOTICE)
      .then(data => {
        const privacyNotice = data.body as PrivacyNotice;

        if (isEmptyObject(privacyNotice)) {
          this.validPrivacyAndTermOfUser = false;
        }
      });
  }

  validateIfPrivacyIsDefined(): void {
    HttpService.get(APPLICATION_ENDPOINTS.CREATE_NEW_ACCOUNT_TERM_OF_USER)
      .then(data => {
        const termOfUser = data.body as TermOfUser;

        if (isEmptyObject(termOfUser)) {
          this.validPrivacyAndTermOfUser = false;
        }
      });
  }

  @OnSubmit({ target: ['wizard-account-creation'] })
  async onSubmitStepsAccountCreation(): Promise<void> {
    const body = this.readAllFormsAndReturnBody();
    this.accountCreationBlackboard.accountData = body;
    this.historyService.push(APPLICATION_ROUTES.ACCOUNT_CREATION_PROCESSING);
  }

  readAllFormsAndReturnBody(): Record<string, Value> {
    return this.allForms.map(form => form.getRecord())
      .map((record: Record<string, Value>) => {
        this.replaceValueWithNestedProperty(record, 'counsilState', 'vlDomain');
        this.replaceValueWithNestedProperty(record, 'medicalSpecialty', 'cdSpecialty');
        this.replaceValueWithNestedProperty(record, 'personGender', 'vlDominio');
        this.replaceValueWithNestedProperty(record, 'personState', 'vlDomain');
        return record;
      })
      .reduce((prev, curr) => Object.assign(prev, curr), {});
  }

  private replaceValueWithNestedProperty(object: Record<string, Value>, propertyName: string, nestedProperty: string): void {
    Object.keys(object)
      .filter(key => key === propertyName)
      .forEach(key => {
        const nestedObject: Record<string, Value> = cast(object[key]);
        object[key] = nestedObject[nestedProperty];
      });
  }

  onClickTerm(): void {
    ModalService.openFullscreen({
      closeable: true,
      fragment: 'account-creation-terms-modal',
      onClose: () => { return },
      title: 'results.terms.use',
    });
  }

  onClickPrivacy(): void {
    ModalService.openFullscreen({
      closeable: true,
      fragment: 'account-creation-privacy-modal',
      onClose: () => { return },
      title: 'newaccount.privacy',
    });
  }

  @OnValidate({ target: ['form-personal-identification', 'form-personal-information', 'form-address', 'form-account-information'] })
  onUserPersonalFormChangeStep(event: ValidateEvent): void {
    switch (this.stepsAccountCreation.getCurrentStepIdentifier()) {
      case StepNames.PersonalIdentification: {
        this.stepsAccountCreation.setStepDone(StepNames.PersonalIdentification, event.valid);
        break;
      }
      case StepNames.PersonalInformation: {
        this.stepsAccountCreation.setStepDone(StepNames.PersonalInformation, event.valid);
        break;
      }
      case StepNames.PersonalAddress: {
        this.stepsAccountCreation.setStepDone(StepNames.PersonalAddress, event.valid);
        break;
      }
      case StepNames.PersonalAccount: {
        this.stepsAccountCreation.setStepDone(StepNames.PersonalAccount, event.valid && !!this.checkboxAgreeTerms.getValue() && this.validPrivacyAndTermOfUser);
        break;
      }
    }
  }

  @OnKeyUp({ target: 'input-taxpayer' })
  maskCpf(): void {
    let value = this.taxpayer.getValue().toString();
    value = value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    this.taxpayer.setValue(value);
  }

  private updateDefaultPhoneDdi(): void {
    HttpService.get(APPLICATION_ENDPOINTS.GET_LOCALE_AND_LANGUAGE)
      .then(r => {
        const { locale } = r.body as { locale: string };
        this.inputPhone.setCountry(locale.slice(-2).toUpperCase() as Country)
      });
  }

  @OnKeyUp({ target: 'input-postal' })
  maskPostal(): void {
    let value = this.postal.getValue().toString();
    value = value.replace(/(\d{5})(\d{3})/, '$1-$2');
    this.postal.setValue(value);
  }

  @OnBlur({ target: 'input-email' })
  onBlurEmail(): void {
    const email = this.accountForm.getRecord().accountEmail as string;

    HttpService.get(APPLICATION_ENDPOINTS.IS_EMAIL_VALID, { email })
      .then(() => {
        this.fieldset.setVisible(true);

        if (this.emailAlreadyExists) {
          this.emailAlreadyExists = false;
          this.emailField.removeValidator('emailAlreadyExistsValidator');
        }
      }).catch(() => {
        this.fieldset.setVisible(false);

        if (!this.emailAlreadyExists) {
          this.emailAlreadyExists = true;
          this.emailField.addValidator({ name: 'emailAlreadyExistsValidator' });
        }
      });
  }

  addValidators(): void {
    this.fieldConfirmEmail.addLocalValidator(CONFIRMATION_FIELD_VALID, () => {
      return confirmationFieldValidator(this.email, this.confirmEmail, 'email')
    }, optionsValidator());
    this.fieldConfirmPassword.addLocalValidator(CONFIRMATION_FIELD_VALID, () => {
      return confirmationFieldValidator(this.password, this.confirmPassword, 'pwd')
    }, optionsValidator());
  }

  @OnMount({ target: AccountCreationStepsAliases.FIELD_CONFIRM_EMAIL })
  onMountConfirmEmail(): void {
    this.addValidators();
  }

  @OnChange({ target: 'input-email' })
  async onChangeEmail(event: ChangeEvent<string>): Promise<void> {
    this.email =  event.value as string ;

    if (this.emailAlreadyExists) {
      this.emailAlreadyExists = false;
      this.emailField.removeValidator('emailAlreadyExistsValidator');
    }

    const confirmEmail = this.inputConfirmEmail?.getValue();

    if (confirmEmail) {
      await this.fieldConfirmEmail.validate();
    }
  }

  @OnChange({ target: 'input-confirm-email' })
  onChangeConfirmEmail(event: ChangeEvent<string>): void {
    this.confirmEmail = event.value as string;
  }

  @OnChange({ target: 'input-password' })
  async onPasswordChange(event: ChangeEvent<string>): Promise<void> {
    this.password = event.value as string;

    const confirmPassword = this.inputConfirmPassword.getValue();

    if (confirmPassword) {
      await this.fieldConfirmPassword.validate();
    }
  }

  @OnChange({ target: 'input-confirm-password' })
  onConfirmPasswordChange(event: ChangeEvent<string>): void {
    this.confirmPassword = event.value as string;
  }

}
